import React from "react";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { nFormatter } from "../../../Helpers";
import { ExdInfoTip } from "../../ExDashboard/components/ExdControls";
import { MD, MD_KPI_TILES } from "./mdTheme";
import { MdMoneyValue } from "./MdMoneyValue";

const MdKpiTile = ({
  accent = "t1",
  title,
  infoTitle,
  value,
  rawValue,
  valueSuffix,
  money = false,
  currency,
  subtitle,
  footer,
  meter = 100,
  loading,
}) => {
  const theme = MD_KPI_TILES[accent] || MD_KPI_TILES.t1;

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          bgcolor: "#fff",
          border: `1px solid ${MD.line}`,
          borderRadius: "10px",
          minHeight: 118,
          p: "12px 14px",
          width: "100%",
        }}
      >
        <Stack spacing={1.25}>
          <Box sx={{ bgcolor: "#eef0f6", borderRadius: 1, height: 14, width: "55%" }} />
          <Box sx={{ bgcolor: "#eef0f6", borderRadius: 1, height: 28, width: "40%" }} />
          <Box sx={{ bgcolor: "#eef0f6", borderRadius: 99, height: 4, width: "100%" }} />
          <Box sx={{ bgcolor: "#eef0f6", borderRadius: 1, height: 12, width: "70%" }} />
        </Stack>
      </Paper>
    );
  }

  const display =
    value != null && value !== ""
      ? typeof value === "number"
        ? nFormatter(value, 2)
        : value
      : "0";

  return (
    <Paper
      elevation={0}
      component="article"
      sx={{
        bgcolor: theme.bgcolor,
        borderRadius: "10px",
        color: theme.color,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        minHeight: 118,
        minWidth: 0,
        overflow: "hidden",
        p: "12px 14px",
        position: "relative",
        width: "100%",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: "100%" }}>
        <Typography
          component="div"
          sx={{
            color: "inherit",
            flex: 1,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.06em",
            lineHeight: 1.25,
            m: 0,
            minWidth: 0,
            opacity: 0.95,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <ExdInfoTip title={infoTitle} tone={theme.infoTone} />
      </Stack>

      {money && currency ? (
        <MdMoneyValue
          amount={value}
          currency={currency}
          fontSize={22}
          color="inherit"
        />
      ) : (
        <Tooltip
          title={
            <Typography sx={{ fontSize: 14 }}>
              {rawValue != null
                ? Number(rawValue).toLocaleString?.() || rawValue
                : display}
            </Typography>
          }
          placement="bottom-start"
        >
          <Typography
            component="div"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              fontFamily: MD.fontHead,
              fontSize: 22,
              fontVariantNumeric: "tabular-nums",
              fontWeight: 700,
              gap: 0.5,
              lineHeight: 1.15,
              m: 0,
              "& small": { fontSize: "0.5em", fontWeight: 600, opacity: 0.9 },
            }}
          >
            {display}
            {valueSuffix ? <Box component="small">{valueSuffix}</Box> : null}
          </Typography>
        </Tooltip>
      )}

      {subtitle ? (
        <Typography
          component="div"
          sx={{
            color: "inherit",
            fontSize: 11,
            m: 0,
            opacity: 0.95,
          }}
        >
          {subtitle}
        </Typography>
      ) : null}

      <Box
        sx={{
          bgcolor: theme.meterTrack,
          borderRadius: 9,
          height: 4,
          mt: "auto",
          overflow: "hidden",
          width: "100%",
        }}
      >
        <Box
          sx={{
            bgcolor: theme.meterBar,
            borderRadius: 9,
            height: "100%",
            width: `${Math.min(Math.max(Number(meter) || 0, 0), 100)}%`,
          }}
        />
      </Box>

      {footer ? (
        <Typography
          component="div"
          sx={{
            color: "inherit",
            fontSize: 10.5,
            fontWeight: 700,
            m: 0,
          }}
        >
          {footer}
        </Typography>
      ) : null}
    </Paper>
  );
};

export default MdKpiTile;
